import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { IoCodeSlashOutline, IoCreateOutline } from "react-icons/io5";
import { useNavigate } from "react-router";

import GetGeometryType from "./GetGeometryType.graphql";
import UpdateGeometryType from "./UpdateGeometryType.graphql";
import useNotifications, { notifyMutationErrors } from "../../../../../components/Notifications";
import * as UI from "../../../../../modules/ui-kit";
import CodeEditor from "../../../components/CodeEditor";
import { StyledEditorLayout } from "../../../components/StyledEditorLayout";
import {
    PropertiesField,
    RequirementGroupsField,
    UrlListField,
} from "../../../components/fields";
import { sameSource } from "../../../modules/compare";
import { useUnsavedChangesEditor } from "../../../modules/unsaved-changes";

export default function Editor(props) {
    const { guard, id, revisionID } = props;
    const navigate = useNavigate();

    const notifications = useNotifications();

    const [mode, setMode] = useState<"form" | "json">("form");

    const getGeometryType = useQuery(
        gql(GetGeometryType),
        {
            variables: {
                id,
                revisionID,
            },
        },
    );

    const rawString = getGeometryType.data?.geometryType?.revision?.raw;
    const storedSource = rawString ? JSON.parse(rawString) : {};

    const [currentSource, setCurrentSource] = useState(undefined);
    const [jsonText, setJsonText] = useState("");
    const [jsonError, setJsonError] = useState(undefined);
    const [saving, setSaving] = useState(false);



    useEffect(
        () => {
            setCurrentSource(undefined);
            setJsonText(JSON.stringify(storedSource, null, 2));
            setJsonError(undefined);
        },
        [revisionID, getGeometryType.data],
    );

    const activeSource = currentSource ?? storedSource;
    const dirty = !sameSource(activeSource, storedSource);

    const [updateGeometryType] = useMutation(
        gql(UpdateGeometryType),
        {
            // Every save appends a revision: the version list has to be back
            // before we navigate to the new one.
            awaitRefetchQueries: true,
            refetchQueries: [
                gql(GetGeometryType),
                "ListGeometryTypeRevisions",
                "ListBimClassGeometryTypes",
            ],
        },
    );


    function updateField(key: string, value: unknown) {
        setCurrentSource({
            ...activeSource,
            [key]: value,
        });
    }

    function onJsonChange(value: string | undefined) {
        const text = value ?? "";
        setJsonText(text);
        try {
            const parsed = JSON.parse(text);
            setCurrentSource(parsed);
            setJsonError(undefined);
        } catch (e) {
            setJsonError((e as Error).message);
        }
    }

    function switchMode(targetMode: "form" | "json") {
        setJsonError(undefined);
        if (targetMode === "json") {
            setJsonText(JSON.stringify(activeSource, null, 2));
        }
        setMode(targetMode);
    }

    async function save() {
        if (jsonError && mode === "json") return false;

        setSaving(true);

        try {
            const result = await updateGeometryType({
                variables: {
                    id,
                    geometryType: {
                        ...activeSource,
                        deprecated: Boolean(activeSource.deprecated),
                    },
                },
            });

            if (result.data?.updateGeometryType?.errors?.length) {
                notifyMutationErrors(notifications, result.data.updateGeometryType.errors);
                return false;
            }

            setCurrentSource(undefined);
            notifications.success("Saved.");

            const newRevisionID = result.data?.updateGeometryType?.geometryTypeRevision?.id;
            if (newRevisionID && newRevisionID !== revisionID) {
                navigate(`../revisions/${ newRevisionID }`);
            }

            return true;
        } finally {
            setSaving(false);
        }
    }

    useUnsavedChangesEditor(guard, save, dirty);

    if (getGeometryType.loading) return "loading";
    if (getGeometryType.error) return "error";

    function onSubmit(event) {
        event.preventDefault();
        void save();
    }

    function onReset() {
        setCurrentSource(undefined);
        setJsonText(JSON.stringify(storedSource, null, 2));
        setJsonError(undefined);
    }

    const requirementGroups = activeSource.requirementGroups ?? [];
    // Stored as `previews`; `images` is only the GraphQL alias for it.
    const previews = activeSource.previews ?? activeSource.images ?? [];
    const properties = activeSource.properties ?? [];











    return (
        <StyledEditorLayout>
            <div className="mode-tabs">
                <button
                    className={ mode === "form" ? "active" : "" }
                    onClick={ () => switchMode("form") }
                    type="button"
                >
                    <IoCreateOutline /> Form View
                </button>
                <button
                    className={ mode === "json" ? "active" : "" }
                    onClick={ () => switchMode("json") }
                    type="button"
                >
                    <IoCodeSlashOutline /> JSON Source
                </button>
            </div>

            <UI.Form
                className="editor-body"
                onReset={ onReset }
                onSubmit={ onSubmit }
            >
                { mode === "form" ? (
                    <>
                        <UI.Field>
                            <UI.FieldLabel>name</UI.FieldLabel>
                            <UI.FieldDescription>Multi-language translations for the Geometry Type name</UI.FieldDescription>
                            <UI.TranslatedInput
                                disabled={ saving }
                                label="name"
                                onChange={ (val) => updateField("name", val) }
                                value={ activeSource.name ?? [] }
                            />
                        </UI.Field>

                        <UrlListField
                            addLabel="Add Preview Image URL"
                            description="Preview image URLs illustrating this Geometry Type"
                            disabled={ saving }
                            label="previews"
                            onChange={ (val) => updateField("previews", val) }
                            value={ previews }
                        />

                        <RequirementGroupsField
                            description="Logical groups organizing parameters for this Geometry Type"
                            disabled={ saving }
                            label="requirementGroups"
                            onChange={ (val) => updateField("requirementGroups", val) }
                            value={ requirementGroups }
                        />

                        <PropertiesField
                            description="Assigned parameter properties and values (parameter.id &amp; value)"
                            disabled={ saving }
                            label="properties"
                            onChange={ (val) => updateField("properties", val) }
                            value={ properties }
                        />

                        {/* Last: a lifecycle switch, not a property being edited. */}
                        <UI.Field>
                            <UI.FieldLabel>deprecated</UI.FieldLabel>
                            <UI.FieldDescription>Toggle whether this Geometry Type is active or deprecated</UI.FieldDescription>
                            <UI.SegmentedControl
                                disabled={ saving }
                                onChange={ (val) => updateField("deprecated", val) }
                                options={ [
                                    { label: "Deprecated", value: true },
                                    { label: "Not deprecated", value: false },
                                ] }
                                value={ Boolean(activeSource.deprecated) }
                            />
                        </UI.Field>
                    </>
                ) : (
                    <UI.Field className="json-field">
                        <UI.FieldLabel>Raw JSON Definition</UI.FieldLabel>
                        <UI.FieldDescription>Edit raw Geometry Type JSON definition (name, deprecated, previews, requirementGroups, requirements, properties, componentTypes)</UI.FieldDescription>
                        <div className="json-editor-wrapper">
                            <CodeEditor
                                key={ `${ id }-${ mode }-${ revisionID }` }
                                onChange={ onJsonChange }
                                value={ jsonText }
                            />
                        </div>
                        { jsonError && <span className="errors">Invalid JSON: { jsonError }</span> }
                    </UI.Field>
                ) }


                <UI.ButtonGroup>
                    <UI.Button
                        disabled={ !dirty || saving }
                        type="reset"
                    >
                        Reset
                    </UI.Button>
                    <UI.Button
                        $appearance="primary"
                        disabled={ !dirty || (mode === "json" && !!jsonError) || saving }
                        type="submit"
                    >
                        { saving ? "Saving…" : "Save" }
                    </UI.Button>
                </UI.ButtonGroup>
            </UI.Form>
        </StyledEditorLayout>
    );
}
